import React, { useEffect, useRef, useState } from "react";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";
import { CircularProgress } from "@mui/material";
import { useShowAlert } from "../../../../../context/AlertContext";
import autoTable from "jspdf-autotable";
import jsPDF from "jspdf";
import InterRegular from "../../../sale/font/Inter_24pt-Regular-normal";
import InterBold from "../../../sale/font/Inter_24pt-Bold-normal";
import { useNetwork } from "../../../../../context/NetworkContext";
import AxiosInstance from "../../../../../utils/AxiosInstance";
import uRLs from "../../../../../utils/baseUtils";
import handleError from "../../../../../constants/handleError";
import { useAuth } from "../../../../../context/AuthProvider";
import { usePermission } from "../../../../../context/PermissionContext";
import CashButton from "../../../sale/cashbtn";
import PayLaterButton from "../../../sale/PaylaterBtn";
import Messages from "../../../../../constants/Messages";
import { FormatedSaleData } from "../../../../../utils/print/GlobelSaleData";
import { DownloadIcon, PrinterIcon, ShowIcon } from "../../../../../constants/Icons";

const InvoiceThumnail1 = ({
  isViewData,
  selectedTheme,
  selectedSize,
  downloadbtn = false,
  payment=false,
}) => {
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const [download, setDownload] = useState(false);
  const [print, setPrint] = useState(false);
  const [data, setData] = useState({});
  const globalData = FormatedSaleData(data);
  const { settings } = useAuth();
  const { storeSettings } = usePermission();
  const [terms, setTerms] = useState([]);
  const cashRef = useRef();
  const paylaterRef = useRef();
  const sizeOptions = [
    { key: "BILL", label: "BILL" },
    { key: "INVOICE", label: "INVOICE" },
  ];

  const formatTrackMode = (salemode) => {
    switch (salemode?.toUpperCase()) {
      case "BILL":
        return "BILL RECIEPT";
      case "INVOICE":
        return "TAX INVOICE";
      default:
        return salemode;
    }
  };

  const formatCurrency = (value) => {
    const formatted = CurrencyConverter.toCurrency(Math.abs(value), "INR");
    return value < 0 ? `-₹${formatted}` : `₹${formatted}`;
  };

  const saleDate = data?.saledate;
  const timezone = data?.saleby?.timezone;

  let saleTime = "--:--";
  if (saleDate) {
    const zoned = DateConverter.convertToTimeZone(saleDate, timezone);
    saleTime = DateConverter.ISOToDisplayFormat(zoned);
  }

  let createdTime = "--:--";
  if (data?.createdAt) {
    const zoned = DateConverter.convertToTimeZone(data.createdAt, timezone);
    createdTime = DateConverter.ISOToDisplayFormat(zoned);
  }

  const loadImageDimensions = (base64Src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () =>
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = reject;
      img.src = base64Src;
    });
  };

  const generateBillingPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const padding = 7;
    const headerX = 2;
    const headerY = 2;
    const imagePath = settings?.saleProfileData?.image
    ? uRLs?.IMAGE_URL + "profile/" + settings?.saleProfileData?.image
    : "/assets/images/logo-light.png";

    if (data?.salefrom?.image) {
      try {
        // Convert image URL to base64
        const response = await fetch(imagePath);
        const blob = await response.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        // Load original dimensions
        const { width: naturalWidth, height: naturalHeight } =
          await loadImageDimensions(base64);

        // Resize logic
        const maxWidth = 50;
        const maxHeight = 10;
        let imgWidth = naturalWidth;
        let imgHeight = naturalHeight;
        const widthRatio = maxWidth / imgWidth;
        const heightRatio = maxHeight / imgHeight;
        const ratio = Math.min(widthRatio, heightRatio);
        imgWidth *= ratio;
        imgHeight *= ratio;

        const getImageFormat = (path) => {
          const ext = path.split(".").pop().toLowerCase();
          if (ext === "jpg" || ext === "jpeg") return "JPEG";
          if (ext === "png") return "PNG";
          return "JPEG";
        };
        const format = getImageFormat(imagePath);

        doc.addImage(
          base64,
          format,
          headerX + padding,
          headerY + padding,
          imgWidth,
          imgHeight,
        );
      } catch (err) {
        console.warn("Image failed to load:", err);
      }
    }

    const invoiceFontSize = 20;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(invoiceFontSize);
    const textHeightOffset = invoiceFontSize * 0.55;
    const invoiceTextY = headerY + textHeightOffset;
    const invoiceX = pageWidth - padding;
    const invoiceText = data.salemode && formatTrackMode(data.salemode);
    const invoiceTextWidth = doc.getTextWidth(invoiceText);
    doc.text(invoiceText, invoiceX, invoiceTextY, { align: "right" });
    if (data.status === "CANCELLED") {
      const cancelledFontSize = invoiceFontSize * 0.6;
      doc.setFontSize(cancelledFontSize);
      doc.setTextColor(220, 53, 69);
      doc.setFont("Inter-Bold", "bold");
      const cancelledX = invoiceX - invoiceTextWidth - 8; // 8 is spacing between words
      doc.text("CANCELLED", cancelledX, invoiceTextY, { align: "right" });
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(invoiceFontSize);
    }
    doc.setFontSize(10);
    const invoiceNoLabel = "Bill No:";
    const invoiceNoValue = data.saleno;
    const invoiceDateLabel = "Date:";
    const invoiceDateValue = DateConverter.ISOToDisplayFormat(
      data.saledate,
    ).date;
    // Measure total widths
    const invoiceNoLabelWidth = doc.getTextWidth(invoiceNoLabel);
    const invoiceNoValueWidth = doc.getTextWidth(invoiceNoValue);
    const invoiceDateLabelWidth = doc.getTextWidth(invoiceDateLabel);
    const invoiceDateValueWidth = doc.getTextWidth(invoiceDateValue);
    const gap = 10;
    const innerGap = 2;
    const invoiceRowY = 25;
    const totalTextWidth =
      invoiceNoLabelWidth +
      innerGap +
      invoiceNoValueWidth +
      gap +
      invoiceDateLabelWidth +
      innerGap +
      invoiceDateValueWidth;
    const invoiceRowX = pageWidth - padding - totalTextWidth;
    const lineStartX = padding;
    const lineEndX = invoiceRowX - 5;
    const lineY = invoiceRowY;
    // Middle line
    doc.setDrawColor(selectedTheme?.templateTheme?.primaryColor);
    doc.setLineWidth(2);
    doc.setLineCap("round");
    doc.line(lineStartX, lineY, lineEndX, lineY);
    doc.setFont("Inter-Bold", "bold");
    doc.text(invoiceNoLabel, invoiceRowX, invoiceRowY);
    doc.setFont("Inter-Regular", "normal");
    doc.text(
      invoiceNoValue,
      invoiceRowX + invoiceNoLabelWidth + innerGap,
      invoiceRowY,
    );
    const dateX =
      invoiceRowX + invoiceNoLabelWidth + innerGap + invoiceNoValueWidth + gap;
    doc.setFont("Inter-Bold", "bold");
    doc.text(invoiceDateLabel, dateX, invoiceRowY);
    doc.setFont("Inter-Regular", "normal");
    doc.text(
      invoiceDateValue,
      dateX + invoiceDateLabelWidth + innerGap,
      invoiceRowY,
    );
    const sectionTop = 35;
    const midX = pageWidth / 2;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(12);
    doc.text("Bill From:", padding, sectionTop);
    doc.setFontSize(9);
    const hasSaleToData =
      data.saleto &&
      (data.saleto.name ||
        data.saleto.addressline1 ||
        data.saleto.addressline2 ||
        data.saleto.district ||
        data.saleto.country ||
        data.saleto.pincode ||
        data.saleto.phone ||
        data.saleto.email ||
        data.saleto.gstnumber ||
        data.saleto.state ||
        data.saleto.statecode ||
        data.saleto.referid);

    if (hasSaleToData) {
      doc.setFontSize(9);
      doc.text("Bill To:", midX, sectionTop);
    }
    doc.setFontSize(9);
    let fromY = sectionTop + 5;
    let toY = sectionTop + 5;
    const fromDetails = [
      { bold: true, text: data.salefrom?.title || "-" },
      // Address will be dynamically split and inserted below
      { bold: false, text: `Phone: ${data.salefrom?.phone || "+91"}` },
      {
        bold: false,
        text: `Email: ${data.salefrom?.email || ""}`,
      },
      {
        bold: true,
        text: `State:`,
        suffix: ` ${data.salefrom?.state || ""} [ State Code: ${
          data.salefrom?.statecode || ""
        } ]`,
      },
    ];
    const sectionGap = 12;
    const sectionWidth = (pageWidth - padding * 2 - sectionGap) / 2 + 20;
    const fromAddressWrapWidth = sectionWidth - 30;
    const rawAddress = [
      data.salefrom?.addressline1,
      data.salefrom?.addressline2,
      data.salefrom?.district,
      data.salefrom?.country,
      data.salefrom?.pincode ? `- ${data.salefrom.pincode}` : "",
    ]
      .filter(Boolean)
      .join(", ");

    // Wrap address to sectionWidth (same used in renderDetails)
    const wrappedAddress = doc.splitTextToSize(
      rawAddress,
      fromAddressWrapWidth,
    );

    // Insert wrapped address lines after the title (index 1)
    wrappedAddress.forEach((line, i) => {
      fromDetails.splice(1 + i, 0, { bold: false, text: line });
    });

    let toDetails = [];
    if (hasSaleToData) {
      toDetails = [
        { bold: true, text: data.saleto?.name || "-" },
        {
          bold: false,
          text: [
            data.saleto?.addressline1,
            data.saleto?.addressline2,
            data.saleto?.district,
            data.saleto?.country,
            data.saleto?.pincode ? `- ${data.saleto.pincode}` : "",
          ]
            .filter(Boolean)
            .join(", "),
        },
        ...(data.saleto?.phone
          ? [{ bold: false, text: `Phone: ${data.saleto?.phone}` }]
          : []),
        ...(data.saleto?.email
          ? [{ bold: false, text: `Email: ${data.saleto?.email}` }]
          : []),
        ...(data.saleto?.gstnumber
          ? [{ bold: true, text: `GST:`, suffix: ` ${data.saleto?.gstnumber}` }]
          : []),
        ...(data.saleto?.state
          ? [
              {
                bold: true,
                text: `State:`,
                suffix: ` ${data.saleto?.state} [ State Code: ${
                  data.saleto?.statecode || ""
                } ]`,
              },
            ]
          : []),
        ...(data.saleto?.referid
          ? [
              {
                bold: true,
                text: "Reference ID:",
                suffix: ` ${data.saleto.referid}`,
              },
            ]
          : []),
      ];
    }
    const wrapText = (text, maxWidth) => doc.splitTextToSize(text, maxWidth);
    const lineHeight = 5;
    const renderDetails = (details, startX, startY) => {
      let y = startY;
      details.forEach(({ bold, text, suffix = "" }) => {
        const fullText = text + suffix;
        const lines = wrapText(fullText, sectionWidth);

        lines.forEach((line, index) => {
          if (index === 0 && suffix && bold) {
            const labelWidth = doc.getTextWidth(text);
            doc.setFont("Inter-Bold", "bold");
            doc.text(text, startX, y);
            doc.setFont("Inter-Regular", "normal");
            doc.text(suffix, startX + labelWidth, y);
          } else {
            doc.setFont(
              bold ? "Inter-Bold" : "Inter-Regular",
              bold ? "bold" : "normal",
            );
            doc.text(line, startX, y);
          }
          y += lineHeight;
        });
      });

      return y;
    };
    fromY = renderDetails(fromDetails, padding, fromY);
    if (hasSaleToData) {
      toY = renderDetails(toDetails, midX, toY);
    }
    // 3. Product Table
    const productBody = data.saleitems?.map((item, i) => {
      const qty = item.quantity || 1;
      const rate = CurrencyConverter.toCurrency(item.soldprice || 0, "INR");
      const total = CurrencyConverter.toCurrency(
        item.lineTotalSoldPrice || 0,
        "INR",
      );
      const desc = [
        `${item.title}`,
        ...(item.imode === "PRODUCT" && item.note ? [item.note] : []),
        ...(item.imode === "SERVICE" && item.description
          ? [item.description]
          : []),
        ...(item.hsnsac
          ? [`${item.imode === "SERVICE" ? "SAC" : "HSN"} : ${item.hsnsac}`]
          : []),

        // `MRP     : ₹${CurrencyConverter.toCurrency(item.mrp || 0, "INR")}`,
        ...(item.discount > 0
          ? [`MRP     : ₹${CurrencyConverter.toCurrency(item.mrp || 0, "INR")}`]
          : []),
        ...(item.discount > 0
          ? [
              `Discount: - ₹${CurrencyConverter.toCurrency(
                item.discount,
                "INR",
              )}`,
            ]
          : []),
      ].join("\n");

      return [
        i + 1,
        desc,
        `₹${formatCurrency(rate, "INR", false, true)}`,
        qty,
        `₹${formatCurrency(total, "INR", false, true)}`,
      ];
    });

    const grandTotal = CurrencyConverter.toCurrency(
      data.grandtotal || 0,
      "INR",
    );
    // Add total row INSIDE the same table
    productBody.push([
      {
        content: "",
        colSpan: 2,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: "Total",
        styles: { halign: "right", fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: data.totalqty,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: `${formatCurrency(grandTotal, "INR", false, true)}`,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
    ]);
    autoTable(doc, {
      startY: Math.max(fromY, toY),
      head: [["S.No", "Description", "Rate", "Qty", "Total"]],
      body: productBody,
      margin: { left: 6, right: 5 }, // add right margin if needed
      tableWidth: doc.internal.pageSize.getWidth() - 10,
      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        fontSize: 9,
        halign: "middle",
        cellPadding: 2,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        lineWidth: 0.2,
        textColor: "#041121",
      },
      headStyles: {
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme?.templateTheme?.foregroundColor,
        fontStyle: "bold",
        font: "Inter-Bold",
        halign: "center",
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: 13, halign: "center", valign: "middle" },
        1: { cellWidth: 130, halign: "left" },
        2: { cellWidth: 20, halign: "right", valign: "middle" },
        3: { cellWidth: 13, halign: "center", valign: "middle" },
        4: { cellWidth: 20, halign: "right", valign: "middle" },
      },
      willDrawCell: function (data) {
        if (data.column.index === 1 && data.section === "body") {
          const itemIndex = data.row.index;
          const item = data.saleitems?.[itemIndex];
          if (!item) return;

          const x = data.cell.x + 2;
          let y = data.cell.y + 4;
          const lineHeight = 4;
          const currency = CurrencyConverter;

          const lines = [
            { type: "title", text: item.title },
            ...(item.imode === "PRODUCT" && item.note
              ? [{ type: "description", text: item.note }]
              : []),
            ...(item.imode === "SERVICE" && item.description
              ? [{ type: "description", text: item.description }]
              : []),
            ...(item.hasnsac
              ? [
                  {
                    label: item.imode === "SERVICE" ? "SAC:" : "HSN:",
                    value: item.hasnsac,
                  },
                ]
              : []),
            {
              label: "MRP:",
              value: `₹${currency.toCurrency(item.mrp || 0, "INR")}`,
            },
            {
              label: "Discount:",
              value: `₹${currency.toCurrency(item.discount || 0, "INR")}`,
            },
          ];

          data.cell.text = [];
          lines.forEach((line) => {
            if (line.type === "title") {
              doc.setFont("Inter-Bold", "bold");
              doc.setFontSize(15);
              doc.setTextColor(0, 0, 0);
              doc.text(line.text, x, y);
              doc.setFontSize(9);
            } else {
              doc.setFont("Inter-Bold", "bold");
              doc.setFontSize(9);
              doc.setTextColor(0, 0, 0);
              doc.text(line.label, x, y);
              doc.setFont("Inter-Regular", "normal");
              doc.setTextColor(80, 80, 80);
              doc.text(line.value, x + 32, y);
            }
            y += lineHeight;
          });
        }
      },

      theme: "grid",
    });
    const isInterState =
      data.salefrom?.statecode &&
      data.saleto?.statecode &&
      data.salefrom.statecode !== data.saleto.statecode;
    const body = [
      [
        {
          content: "Total Amount In Words",
          styles: {
            fontStyle: "bold",
            font: "Inter-Bold",
            fontSize: 9,
            valign: "top",
          },
        },
        {
          content: "Grand Total",
          styles: { halign: "right", fontSize: 9 },
        },
        {
          content: `${formatCurrency(grandTotal, "INR", false, true)}`,
          styles: { halign: "right", valign: "middle", fontSize: 9 },
        },
      ],
      [
        {
          content: data.amountinwords,
          rowSpan: isInterState ? 3 : 4,
          styles: {
            halign: "center",
            valign: "middle",
            fontSize: 9,
          },
        },
        {
          content: "Grand Total (Rounded)",
          styles: { fontStyle: "bold", halign: "right", fontSize: 9 },
        },
        {
          content: `${formatCurrency(grandTotal, "INR", false, true)}`,
          styles: {
            halign: "right",
            valign: "middle",
            fontStyle: "bold",
            fontSize: 10,
          },
        },
      ],
    ];
    const afterProductsY = doc.lastAutoTable?.finalY + 0.4;
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginBottom = 20;
    const estimatedSummaryHeight = 25;
    let summaryStartY;
    let isSamePage = true;
    if (afterProductsY + estimatedSummaryHeight + marginBottom <= pageHeight) {
      summaryStartY = afterProductsY;
    } else {
      doc.addPage();
      summaryStartY = 20;
      isSamePage = false;
    }
    autoTable(doc, {
      startY: summaryStartY,
      body: body,
      pageBreak: "avoid",
      margin: { left: 6, right: 5 },
      tableWidth: doc.internal.pageSize.getWidth() - 10,
      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        halign: "middle",
        cellPadding: 2,
        fontSize: 9,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        lineWidth: 0.2,
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme.templateTheme?.foregroundColor,
      },
      columnStyles: {
        0: { cellWidth: 103 },
        1: { cellWidth: 73, halign: "right" },
        2: { cellWidth: 20, halign: "center", valign: "middle" },
      },
      didParseCell: function (data) {
        if (data.row.index === 0 && isSamePage) {
          data.cell.styles.lineWidth = {
            top: 0,
            right: 0.2,
            bottom: 0.2,
            left: 0.2,
          };
        }
        const lastRowIndex = body.length - 1;
        if (data.section === "body") {
          if (
            data.row.index === lastRowIndex &&
            (data.column.index === 1 || data.column.index === 2)
          ) {
            // data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.font = "Inter-Bold";
            data.cell.styles.fontStyle = "bold";
          } else {
            data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.fillColor = "#ffffff";
            data.cell.styles.font = "Inter-Regular";
            data.cell.styles.fontStyle = "normal";
          }
        }
      },
      theme: "grid",
    });
    const afterSummaryY = doc.lastAutoTable.finalY;
    const pageHeight2 = doc.internal.pageSize.getHeight();
    const marginBottom2 = 20;
    const declarationHeight = 45;
    let finalY;
    if (afterSummaryY + declarationHeight + marginBottom2 > pageHeight2) {
      doc.addPage();
      finalY = 10;
    } else {
      finalY = afterSummaryY;
    }
    let currentY = finalY + 8;
    const TotalDiscount = CurrencyConverter.toCurrency(
      data.totaldiscount || 0,
      "INR",
    );
    if (parseFloat(data.totaldiscount) > 0) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(10);
      doc.text(
        `Your Total Discount : ${formatCurrency(TotalDiscount, "INR", true, true)}`,
        105,
        currentY,
        { align: "center" },
      );

      currentY += 2; // ONLY if discount exists
    }

    // ---------------- PAYMENT MODE TABLE (LEFT – 2 INCH STYLE) ----------------

    const paymentRows = [];

    const cashPaid = (data?.cashamount || 0) - (data?.cashchange || 0);

    const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

    const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

    const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

    const balanceDue =
      (data?.paylateramount || 0) - (data?.paylaterchange || 0);

    const cashAmt = CurrencyConverter.toCurrency(cashPaid || 0, "INR");
    const cardAmt = CurrencyConverter.toCurrency(cardPaid || 0, "INR");
    const onlineAmt = CurrencyConverter.toCurrency(onlinePaid || 0, "INR");
    const upiAmt = CurrencyConverter.toCurrency(upiPaid || 0, "INR");
    const balanceAmt = CurrencyConverter.toCurrency(balanceDue || 0, "INR");

    // condition based rows
    if (cashAmt > 0) {
      paymentRows.push(["Cash", formatCurrency(cashAmt, "INR", false, true)]);
    }

    if (cardAmt > 0) {
      paymentRows.push(["Card", formatCurrency(cardAmt, "INR", false, true)]);
    }

    if (onlineAmt > 0) {
      paymentRows.push([
        "Online Amount",
        formatCurrency(onlineAmt, "INR", false, true),
      ]);
    }

    if (upiAmt > 0) {
      paymentRows.push(["UPI", formatCurrency(upiAmt, "INR", false, true)]);
    }

    if (balanceAmt > 0) {
      paymentRows.push([
        "Balance Due",
        formatCurrency(balanceAmt, "INR", false, true),
      ]);
    }

    // draw table ONLY if rows exist
    if (paymentRows.length > 0) {
      autoTable(doc, {
        startY: currentY,
        head: [["Payment Mode", ""]],
        body: paymentRows,
        margin: { left: 6 },
        tableWidth: 70,
        styles: {
          font: "Inter-Regular",
          fontSize: 8,
          cellPadding: 2,
          lineColor: selectedTheme?.templateTheme?.borderColor,
          lineWidth: 0.2,
          textColor: "#000",
        },
        headStyles: {
          fillColor: selectedTheme?.templateTheme?.primaryColor,
          textColor: selectedTheme?.templateTheme?.foregroundColor,
          font: "Inter-Bold",
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 40, halign: "left" },
          1: { cellWidth: 30, halign: "right" },
        },
        theme: "grid",
      });

      currentY = doc.lastAutoTable.finalY + 6;
    }

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text("***** Thank You! Visit Us Again *****", pageWidth / 2, currentY, {
      align: "center",
    });
    if (storeSettings?.negobillBranding) {
      currentY += 5;
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(10);
      doc.text("Powerd By", pageWidth / 2, currentY, { align: "center" });
      currentY += 5;
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(10);
      doc.text("www.NegoBill.com", pageWidth / 2, currentY, {
        align: "center",
      });
    }
    currentY += 8;

    const contentWidth = pageWidth - padding * 2;

    // 70% for Terms
    const termsWidth = contentWidth * 0.7;

    // 30% for Signature
    const signWidth = contentWidth * 0.3;

    const leftX = padding;

    const startY = currentY;
    if (terms?.length > 0) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.text("Terms & Conditions", leftX, startY);

      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      const splitTerms = doc.splitTextToSize(terms, termsWidth - 5);
      doc.text(splitTerms, leftX, startY + 4);
    }

    const rightX = pageWidth - padding;

    const boxTopY = currentY + 3;

    // Vertical positions
    const SIGN_Y = boxTopY; // Signature image
    const LABEL_Y = SIGN_Y + 8; // "Issued By, Signature"
    const STORE_Y = LABEL_Y + 4; // Store name

    // Fonts
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(8);

    // -------- 1️⃣ Signature Image FIRST ----------
    const signimg = uRLs.IMAGE_URL + storeSettings?.invoiceSettings?.signature;

    if (storeSettings?.invoiceSettings?.signature) {
      try {
        const response = await fetch(signimg);
        const blob = await response.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        const { width, height } = await loadImageDimensions(base64);

        const maxWidth = 50;
        const maxHeight = 10;

        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const imgWidth = width * ratio;
        const imgHeight = height * ratio;

        const format = signimg.endsWith(".png") ? "PNG" : "JPEG";
        const imgX = rightX - imgWidth;

        doc.addImage(base64, format, imgX, SIGN_Y, imgWidth, imgHeight);
      } catch (err) {
        console.warn("Image failed to load:", err);
      }
    }

    // -------- 2️⃣ Issued By, Signature ----------
    doc.text("Issued By, Signature", rightX, LABEL_Y + 4, { align: "right" });

    // -------- 3️⃣ Store Name ----------
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);

    const titleText = String(data?.salefrom?.title || "");
    doc.text(titleText, rightX, STORE_Y + 4, { align: "right" });

    setDownload(false);
    // setPrint(false);
    return doc;
  };

  const generateinvoicePDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const padding = 7;
    const headerX = 2;
    const headerY = 2;
    const imagePath = settings?.saleProfileData?.image
    ? uRLs?.IMAGE_URL + "profile/" + settings?.saleProfileData?.image
    : "/assets/images/logo-light.png";

    if (globalData?.salefrom?.image) {
      try {
        // Convert image URL to base64
        const response = await fetch(imagePath);
        const blob = await response.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        // Load original dimensions
        const { width: naturalWidth, height: naturalHeight } =
          await loadImageDimensions(base64);

        // Resize logic
        const maxWidth = 50;
        const maxHeight = 10;
        let imgWidth = naturalWidth;
        let imgHeight = naturalHeight;
        const widthRatio = maxWidth / imgWidth;
        const heightRatio = maxHeight / imgHeight;
        const ratio = Math.min(widthRatio, heightRatio);
        imgWidth *= ratio;
        imgHeight *= ratio;

        // Determine format
        const getImageFormat = (path) => {
          const ext = path.split(".").pop().toLowerCase();
          if (ext === "jpg" || ext === "jpeg") return "JPEG";
          if (ext === "png") return "PNG";
          return "JPEG";
        };
        const format = getImageFormat(imagePath);

        // Add image to PDF
        doc.addImage(
          base64,
          format,
          headerX + padding,
          headerY + padding,
          imgWidth,
          imgHeight,
        );
      } catch (err) {
        console.warn("Image failed to load:", err);
      }
    }

    const invoiceFontSize = 20;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(invoiceFontSize);
    // Estimate vertical alignment by adjusting for font size
    const textHeightOffset = invoiceFontSize * 0.55;
    const invoiceTextY = headerY + textHeightOffset;
    const invoiceX = pageWidth - padding;
    const invoiceText = globalData.salemode && formatTrackMode(globalData.salemode);
    const invoiceTextWidth = doc.getTextWidth(invoiceText);
    doc.text(invoiceText, invoiceX, invoiceTextY, { align: "right" });
    // If cancelled, show "CANCELLED" immediately to the right of "INVOICE"
    if (globalData.status === "CANCELLED") {
      const cancelledFontSize = invoiceFontSize * 0.6;
      doc.setFontSize(cancelledFontSize);
      doc.setTextColor(220, 53, 69); // Bootstrap danger red
      doc.setFont("Inter-Bold", "bold");
      // Position CANCELLED right after INVOICE
      const cancelledX = invoiceX - invoiceTextWidth - 8; // 8 is spacing between words
      doc.text("CANCELLED", cancelledX, invoiceTextY, { align: "right" });
      // Reset styles
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(invoiceFontSize);
    }
    // Prepare font size for labels and values
    doc.setFontSize(10);
    const invoiceNoLabel = "Invoice No:";
    const invoiceNoValue = globalData.saleno;
    const invoiceDateLabel = "Date:";
    const invoiceDateValue = DateConverter.ISOToDisplayFormat(
      globalData.saledate,
    ).date;
    // Measure total widths
    const invoiceNoLabelWidth = doc.getTextWidth(invoiceNoLabel);
    const invoiceNoValueWidth = doc.getTextWidth(invoiceNoValue);
    const invoiceDateLabelWidth = doc.getTextWidth(invoiceDateLabel);
    const invoiceDateValueWidth = doc.getTextWidth(invoiceDateValue);
    const gap = 10;
    const innerGap = 2;
    const invoiceRowY = 25;
    const totalTextWidth =
      invoiceNoLabelWidth +
      innerGap +
      invoiceNoValueWidth +
      gap +
      invoiceDateLabelWidth +
      innerGap +
      invoiceDateValueWidth;
    const invoiceRowX = pageWidth - padding - totalTextWidth;
    const lineStartX = padding;
    const lineEndX = invoiceRowX - 5;
    const lineY = invoiceRowY;
    // Middle line
    doc.setDrawColor(selectedTheme?.templateTheme?.primaryColor);
    doc.setLineWidth(2);
    doc.setLineCap("round");
    doc.line(lineStartX, lineY, lineEndX, lineY);

    doc.setFont("Inter-Bold", "bold");
    doc.text(invoiceNoLabel, invoiceRowX, invoiceRowY);
    doc.setFont("Inter-Regular", "normal");
    doc.text(
      invoiceNoValue,
      invoiceRowX + invoiceNoLabelWidth + innerGap,
      invoiceRowY,
    );
    const dateX =
      invoiceRowX + invoiceNoLabelWidth + innerGap + invoiceNoValueWidth + gap;

    doc.setFont("Inter-Bold", "bold");
    doc.text(invoiceDateLabel, dateX, invoiceRowY);

    doc.setFont("Inter-Regular", "normal");
    doc.text(
      invoiceDateValue,
      dateX + invoiceDateLabelWidth + innerGap,
      invoiceRowY,
    );
    const sectionTop = 35;
    const col1X = padding;
    const col2X = pageWidth / 3 + 2;
    const col3X = (pageWidth / 3) * 2 + 2;
    const midX = pageWidth / 2;
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text("Invoice From:", padding, sectionTop);
    doc.setFontSize(8);
    const hasSaleToData = 
      globalData.saleto &&
      (globalData.saleto.name ||
        globalData.saleto.addressline1 ||
        globalData.saleto.addressline2 ||
        globalData.saleto.district ||
        globalData.saleto.country ||
        globalData.saleto.pincode ||
        globalData.saleto.phone ||
        globalData.saleto.email ||
        globalData.saleto.gstnumber ||
        globalData.saleto.state ||
        globalData.saleto.statecode ||
        globalData.saleto.referid);

        const hasShipping =
        globalData.saleto?.shippingAddress &&
        ( globalData.saleto?.shippingAddress?.addressline1 ||
          globalData.saleto?.shippingAddress?.addressline2);
  
          if (hasSaleToData) {
            doc.setFontSize(10);
            doc.text("Invoice To:", col2X, sectionTop);
          }
          if (hasShipping) {
            doc.setFontSize(10);
            doc.setFont("Inter-Bold", "bold");
            doc.text("Shipping To:", col3X, sectionTop);
          }
    doc.setFontSize(8);
    let fromY = sectionTop + 5;
    let toY = sectionTop + 5;
    const fromDetails = [
      { bold: true, text: globalData.salefrom?.title || "-" },
      // Address will be dynamically split and inserted below
      { bold: false, text: `Phone: ${globalData.salefrom?.phone || "+91"}` },
      {
        bold: false,
        text: `Email: ${globalData.salefrom?.email || ""}`,
      },
      {
        bold: true,
        text: `GST:`,
        suffix: ` ${globalData.salefrom?.gstnumber || ""}`,
      },
      {
        bold: true,
        text: `FSSAI:`,
        suffix: ` ${globalData.salefrom?.fssai || ""}`,
      },
      {
        bold: true,
        text: `PAN:`,
        suffix: ` ${globalData.salefrom?.pannumber || ""}`,
      },
      {
        bold: true,
        text: `State:`,
        suffix: ` ${globalData.salefrom?.state || ""} [ State Code: ${
          globalData.salefrom?.statecode || ""
        } ]`,
      },
    ];
    const sectionGap = 12;
    const sectionWidth = pageWidth / 3 - padding - 4;
    const fromAddressWrapWidth = sectionWidth - 10;
    const rawAddress = [
      globalData.salefrom?.addressline1,
      globalData.salefrom?.addressline2,
      globalData.salefrom?.district,
      globalData.salefrom?.country,
      globalData.salefrom?.pincode ? `- ${globalData.salefrom.pincode}` : "",
    ]
      .filter(Boolean)
      .join(", ");

    // Wrap address to sectionWidth (same used in renderDetails)
    const wrappedAddress = doc.splitTextToSize(
      rawAddress,
      fromAddressWrapWidth,
    );

    // Insert wrapped address lines after the title (index 1)
    wrappedAddress.forEach((line, i) => {
      fromDetails.splice(1 + i, 0, { bold: false, text: line });
    });

    let toDetails = [];
    if (hasSaleToData) {
      toDetails = [
        { bold: true, text: globalData.saleto?.name || "-" },
        {
          bold: false,
          text: [
            globalData.saleto?.addressline1,
            globalData.saleto?.addressline2,
            globalData.saleto?.district,
            globalData.saleto?.country,
            globalData.saleto?.pincode ? `- ${globalData.saleto.pincode}` : "",
          ]
            .filter(Boolean)
            .join(", "),
        },
        ...(globalData.saleto?.phone
          ? [{ bold: false, text: `Phone: ${globalData.saleto?.phone}` }]
          : []),
        ...(globalData.saleto?.email
          ? [{ bold: false, text: `Email: ${globalData.saleto?.email}` }]
          : []),
        ...(globalData.saleto?.gstnumber
          ? [{ bold: true, text: `GST:`, suffix: ` ${globalData.saleto?.gstnumber}` }]
          : []),
        ...(globalData.saleto?.state
          ? [
              {
                bold: true,
                text: `State:`,
                suffix: ` ${globalData.saleto?.state} [ State Code: ${
                  globalData.saleto?.statecode || ""
                } ]`,
              },
            ]
          : []),
        ...(globalData.saleto?.referid
          ? [
              {
                bold: true,
                text: "Reference ID:",
                suffix: ` ${globalData.saleto.referid}`,
              },
            ]
          : []),
      ];
    }
    const wrapText = (text, maxWidth) => doc.splitTextToSize(text, maxWidth);
    const lineHeight = 4;

    const renderDetails = (details, startX, startY) => {
      let y = startY;
      details.forEach(({ bold, text, suffix = "" }) => {
        const fullText = text + suffix;
        const lines = wrapText(fullText, sectionWidth);
        lines.forEach((line, index) => {
          if (index === 0 && suffix && bold) {
            const labelWidth = doc.getTextWidth(text);
            doc.setFont("Inter-Bold", "bold");
            doc.text(text, startX, y);
            doc.setFont("Inter-Regular", "normal");
            doc.text(suffix, startX + labelWidth, y);
          } else {
            doc.setFont(
              bold ? "Inter-Bold" : "Inter-Regular",
              bold ? "bold" : "normal",
            );
            doc.text(line, startX, y);
          }
          y += lineHeight;
        });
      });

      return y;
    };
    fromY = renderDetails(fromDetails, col1X, fromY);
let shippingY = sectionTop + 5;
if (hasSaleToData ) {
  toY = renderDetails(toDetails, col2X, toY);
}
if (hasShipping) {
  const shippingDetails = [
    {
      bold: false,
      text: [
        globalData.saleto?.shippingAddress?.addressline1,
        globalData.saleto?.shippingAddress?.addressline2,
        globalData.saleto?.shippingAddress?.district,
        globalData.saleto?.shippingAddress?.state,
        globalData.saleto?.shippingAddress?.country,
      ].filter(Boolean).join(", "),
    },
    ...(globalData.saleto?.shippingAddress?.pincode
      ? [{ bold: false, text: `INDIA - ${globalData.saleto?.shippingAddress?.pincode}` }]
      : []),
    ...(globalData.saleto?.shippingAddress?.state
      ? [{
          bold: true,
          text: "State:",
          suffix: ` ${globalData?.saleto?.shippingAddress?.state} [ State Code: ${globalData.saleto?.shippingAddress?.statecode || ""} ]`,
        }]
      : []),
  ];
  shippingY = renderDetails(shippingDetails, col3X, shippingY);
}
// Make table start below all three columns
const maxSectionY = Math.max(fromY, toY, shippingY);
    // 3. Product Table
    const productBody = globalData.saleitems?.map((item, i) => {
      const qty = item.quantity || 1;
      const rate = item.soldprice;
      const gross = 
        item.lineTotalSoldPrice ;
      const taxable = 
        item.lineTotalBasePrice;
      const gstAmt = 
        item.lineTotalTaxAmount;
      const total = 
        item.lineTotalSoldPrice ;
        const landingCost=item?.lineTotalLandingCost
      const desc = [
        `${item.title}`,
        ...(item.imode === "PRODUCT" && item.note ? [item.note] : []),
        ...(item.imode === "SERVICE" && item.description
          ? [item.description]
          : []),
        ...(item.hsnsac
          ? [
              {
                label: item.imode === "SERVICE" ? "SAC:" : "HSN:",
                value: item.hsnsac,
              },
            ]
          : []),
        ...(item.discount > 0
          ? [`MRP     : ₹${item.mrp}`]
          : []),
        ...(item.discount > 0
          ? [
              `Discount: - ₹${
                item.discount}`,
            ]
          : []),
        `GST     : ${item.taxpercentage}%`,
      ].join("\n");
      return [
        i + 1,
        desc,
        `₹${rate}`,
        `${qty} ${(() => {
          const unitCode = item?.isSecondaryUnit
            ? item?.unitConfig?.secondaryUnitCode
            : item?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}` ,
        `₹${gross}`,
        `₹${taxable}`,
        `₹${gstAmt}`,
        `₹${total}`,
      ];
    });

    const soldTotal = 
      globalData.totalsoldprice ;

    const baseTotal = 
      globalData.totalbaseprice ;

    const taxTotal = 
      globalData.totaltaxamount ;

    const grandTotal = 
      globalData.grandtotal ;

    // Add total row INSIDE the same table
    productBody.push([
      {
        content: "",
        colSpan: 2,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: "Total",
        styles: { halign: "right", fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: "",
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: `₹${soldTotal}`,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: `₹${baseTotal}`,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: `₹${taxTotal}`,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
      {
        content: `₹${grandTotal}`,
        styles: { fontStyle: "bold", font: "Inter-Bold" },
      },
    ]);
    autoTable(doc, {
      startY: maxSectionY,
      head: [
        [
          "S.No",
          "Description",
          "Rate",
          "Qty",
          "Gross Amount",
          "Taxable Value",
          "GST Amount",
          "Total",
        ],
      ],
      body: productBody,
      margin: { left: 6, right: 5 }, // add right margin if needed
      tableWidth: doc.internal.pageSize.getWidth() - 10,
      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        fontSize: 9,
        halign: "middle",
        cellPadding: 2,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        lineWidth: 0.2,
        textColor: "#041121",
      },
      headStyles: {
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme.templateTheme?.foregroundColor,
        fontStyle: "bold",
        font: "Inter-Bold",
        halign: "center",
        valign: "middle",
      },
      columnStyles: {
        0: { cellWidth: 13, halign: "center", valign: "middle" },
        1: { cellWidth: 73, halign: "left" },
        2: { cellWidth: 20, halign: "right", valign: "middle" },
        3: { cellWidth: 13, halign: "center", valign: "middle" },
        4: { cellWidth: 19, halign: "right", valign: "middle" },
        5: { cellWidth: 19, halign: "right", valign: "middle" },
        6: { cellWidth: 19, halign: "right", valign: "middle" },
        7: { cellWidth: 20, halign: "right", valign: "middle" },
       
      },
      willDrawCell: function (data) {
        if (data.column.index === 1 && data.section === "body") {
          const itemIndex = data.row.index;
          const item = data.saleitems?.[itemIndex];
          if (!item) return;

          const x = data.cell.x + 2;
          let y = data.cell.y + 4;
          const lineHeight = 4;
          const currency = CurrencyConverter;

          const lines = [
            { type: "title", text: item.title },
            ...(item.imode === "PRODUCT" && item.note
              ? [{ type: "description", text: item.note }]
              : []),
            ...(item.imode === "SERVICE" && item.description
              ? [{ type: "description", text: item.description }]
              : []),
            ...(item.hasnsac
              ? [
                  {
                    label: item.imode === "SERVICE" ? "SAC:" : "HSN:",
                    value: item.hasnsac,
                  },
                ]
              : []),
            {
              label: "MRP:",
              value: `₹${item.mrp }`,
            },
            {
              label: "Discount:",
              value: `₹${item.discount }`,
            },
            {
              label: "GST:",
              value: `${item.taxpercentage || 0}%`,
            },
          ];
          data.cell.text = [];
          lines.forEach((line) => {
            if (line.type === "title") {
              doc.setFont("Inter-Bold", "bold");
              doc.setFontSize(15);
              doc.setTextColor(0, 0, 0);
              doc.text(line.text, x, y);
              doc.setFontSize(9);
            } else {
              doc.setFont("Inter-Bold", "bold");
              doc.setFontSize(9);
              doc.setTextColor(0, 0, 0);
              doc.text(line.label, x, y);
              doc.setFont("Inter-Regular", "normal");
              doc.setTextColor(80, 80, 80);
              doc.text(line.value, x + 32, y);
            }
            y += lineHeight;
          });
        }
      },

      theme: "grid",
    });

    const totalBase = 
      globalData.totalbaseprice ;

    const igst = globalData.igstamount;

    const sgst = globalData.sgstamount;

    const cgst = globalData.cgstamount;

    const totalSold = 
      globalData.totalsoldprice ;

    const isInterState =
      globalData.salefrom?.statecode &&
      globalData.saleto?.statecode &&
      globalData.salefrom.statecode !== globalData.saleto.statecode;
    const body = [
      [
        {
          content: "Total Amount In Words",
          styles: {
            fontStyle: "bold",
            font: "Inter-Bold",
            fontSize: 9,
            valign: "top",
          },
        },
        {
          content: "Total Amount Before Tax",
          styles: { halign: "right", fontSize: 9 },
        },
        {
          content: `₹${totalBase}`,
          styles: { halign: "right", valign: "middle", fontSize: 9 },
        },
      ],
      [
        {
          content: globalData.amountinwords,
          rowSpan: isInterState ? 3 : 4,
          styles: {
            halign: "center",
            valign: "middle",
            fontSize: 9,
          },
        },
        {
          content: isInterState ? "IGST" : "SGST",
          styles: { halign: "right", fontSize: 9 },
        },
        {
          content: 
            isInterState ? igst : sgst,
          styles: { halign: "right", valign: "middle", fontSize: 9 },
        },
      ],
    ];
    if (!isInterState) {
      body.push([
        { content: "CGST", styles: { halign: "right", fontSize: 9 } },
        {
          content: `₹${cgst}`,
          styles: { halign: "right", valign: "middle", fontSize: 9 },
        },
      ]);
    }
    // Add Total row
    body.push([
      { content: "Total", styles: { halign: "right", fontSize: 9 } },
      {
        content: `₹${totalSold}`,
        styles: { halign: "right", valign: "middle", fontSize: 9 },
      },
    ]);
    // Add Grand Total row
    body.push([
      {
        content: "Grand Total (Rounded)",
        styles: { fontStyle: "bold", halign: "right", fontSize: 9 },
      },
      {
        content: `₹${grandTotal}`,
        styles: {
          halign: "right",
          valign: "middle",
          fontStyle: "bold",
          fontSize: 10,
        },
      },
    ]);
    const afterProductsY = doc.lastAutoTable?.finalY + 0.3;
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginBottom = 20;
    const estimatedSummaryHeight = 50;
    let summaryStartY;
    let isSamePage = true;
    if (afterProductsY + estimatedSummaryHeight + marginBottom <= pageHeight) {
      summaryStartY = afterProductsY;
    } else {
      doc.addPage();
      summaryStartY = 20;
      isSamePage = false;
    }
    autoTable(doc, {
      startY: summaryStartY,
      body: body,
      pageBreak: "avoid",
      margin: { left: 6, right: 5 },
      tableWidth: doc.internal.pageSize.getWidth() - 10,
      styles: {
        font: "Inter-Regular",
        fontStyle: "normal",
        halign: "middle",
        cellPadding: 2,
        fontSize: 9,
        lineColor: selectedTheme?.templateTheme?.borderColor,
        lineWidth: 0.2,
        fillColor: selectedTheme?.templateTheme?.primaryColor,
        textColor: selectedTheme.templateTheme?.foregroundColor,
      },
      columnStyles: {
        0: { cellWidth: 103 },
        1: { cellWidth: 73, halign: "right" },
        2: { cellWidth: 20, halign: "center", valign: "middle" },
      },
      didParseCell: function (data) {
        if (data.row.index === 0 && isSamePage) {
          data.cell.styles.lineWidth = {
            top: 0,
            right: 0.2,
            bottom: 0.2,
            left: 0.2,
          };
        }
        const lastRowIndex = body.length - 1;
        if (data.section === "body") {
          if (
            data.row.index === lastRowIndex &&
            (data.column.index === 1 || data.column.index === 2)
          ) {
            // data.cell.styles.fillColor = "#f4f4f6";
            // data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.font = "Inter-Bold";
            data.cell.styles.fontStyle = "bold";
          } else {
            data.cell.styles.textColor = [0, 0, 0];
            data.cell.styles.fillColor = "#ffffff";
            data.cell.styles.font = "Inter-Regular";
            data.cell.styles.fontStyle = "normal";
          }
        }
      },
      theme: "grid",
    });
    const afterSummaryY = doc.lastAutoTable.finalY;
    const pageHeight2 = doc.internal.pageSize.getHeight();
    const marginBottom2 = 20;
    const declarationHeight = 45;
    let finalY;
    if (afterSummaryY + declarationHeight + marginBottom2 > pageHeight2) {
      doc.addPage();
      finalY = 10;
    } else {
      finalY = afterSummaryY;
    }
    let currentY = finalY + 8;
    const TotalDiscount = globalData.totaldiscount ;
    if ((globalData.totaldiscount) > 0) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(10);
      doc.text(
        `Your Total Discount : ₹${TotalDiscount}`,
        105,
        currentY,
        { align: "center" },
      );

      currentY += 2; // ONLY if discount exists
    }

    // ---------------- PAYMENT MODE TABLE (LEFT – 2 INCH STYLE) ----------------
    const paymentRows = [];

    const cashPaid = (globalData?.cashamount || 0) - (data?.cashchange || 0);

    const cardPaid = (data?.cardamount || 0) - (data?.cardchange || 0);

    const onlinePaid = (data?.onlineamount || 0) - (data?.onlinechange || 0);

    const upiPaid = (data?.upiamount || 0) - (data?.upichange || 0);

    const balanceDue =
      (data?.paylateramount || 0) - (data?.paylaterchange || 0);

    const cashAmt = globalData.cashPaid ;
    const cardAmt = globalData.cardPaid ;
    const onlineAmt = globalData.onlinePaid ;
    const upiAmt = globalData.upiPaid ;
    const balanceAmt = globalData.balanceDue ;

    // condition based rows
    if (cashAmt > 0) {
      paymentRows.push(["Cash", `₹${cashAmt}`]);
    }

    if (cardAmt > 0) {
      paymentRows.push(["Card", `₹${cardAmt}`]);
    }

    if (onlineAmt > 0) {
      paymentRows.push([
        "Online Amount",
        `₹${onlineAmt}`,
      ]);
    }

    if (upiAmt > 0) {
      paymentRows.push(["UPI", `₹${upiAmt}`]);
    }

    if (balanceAmt > 0) {
      paymentRows.push([
        "Balance Due",
        `₹${balanceAmt}`,
      ]);
    }

    // draw table ONLY if rows exist
    if (paymentRows.length > 0) {
      autoTable(doc, {
        startY: currentY,
        head: [["Payment Mode", ""]],
        body: paymentRows,
        margin: { left: 6 },
        tableWidth: 70,
        styles: {
          font: "Inter-Regular",
          fontSize: 8,
          cellPadding: 2,
          lineColor: selectedTheme?.templateTheme?.borderColor,
          lineWidth: 0.2,
          textColor: "#000",
        },
        headStyles: {
          fillColor: selectedTheme?.templateTheme?.primaryColor,
          textColor: selectedTheme?.templateTheme?.foregroundColor,
          font: "Inter-Bold",
          fontStyle: "bold",
          halign: "center",
        },
        columnStyles: {
          0: { cellWidth: 40, halign: "left" },
          1: { cellWidth: 30, halign: "right" },
        },
        theme: "grid",
      });

      currentY = doc.lastAutoTable.finalY + 6;
    }

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(10);
    doc.text("***** Thank You! Visit Us Again *****", pageWidth / 2, currentY, {
      align: "center",
    });
    if (storeSettings?.negobillBranding) {
      currentY += 5;
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(10);
      doc.text("Powerd By", pageWidth / 2, currentY, { align: "center" });
      currentY += 5;
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(10);
      doc.text("www.NegoBill.com", pageWidth / 2, currentY, {
        align: "center",
      });
    }
    currentY += 8;

    const contentWidth = pageWidth - padding * 2;

    // 70% for Terms
    const termsWidth = contentWidth * 0.7;

    // 30% for Signature
    const signWidth = contentWidth * 0.3;

    const leftX = padding;

    const startY = currentY;
    if (terms?.length > 0) {
      doc.setFont("Inter-Bold", "bold");
      doc.setFontSize(9);
      doc.text("Terms & Conditions", leftX, startY);

      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      const splitTerms = doc.splitTextToSize(terms, termsWidth - 5);
      doc.text(splitTerms, leftX, startY + 4);
    }
    // const boxTopY = footerY + 10;
    // const boxHeight = 25;
    // const boxLeftX = padding;
    // const boxRightX = pageWidth - padding;
    // const boxWidth = boxRightX - boxLeftX;
    // const boxMidX = boxLeftX + boxWidth / 2;

    // doc.setFontSize(10);
    // doc.setDrawColor(selectedTheme?.templateTheme?.borderColor);
    // doc.setLineWidth(0.2);
    // doc.rect(boxLeftX, boxTopY, boxWidth, boxHeight);
    // doc.line(boxMidX, boxTopY, boxMidX, boxTopY + boxHeight);

    // const startY = boxTopY + 6;
    // const lineHeight2 = 5;
    // const leftContentWidth = boxMidX - boxLeftX - 10;
    // const rightContentWidth = boxRightX - boxMidX - 10;

    // doc.setFont("Inter-Bold", "bold");
    // doc.text("Declaration:", boxLeftX + 5, startY);

    // let declarationText =
    //   "We declare that this tax invoice shows the actual price of the goods described and that all particulars are true and correct.";
    // doc.setFont("Inter-Regular", "normal");
    // const leftLines = doc.splitTextToSize(declarationText, leftContentWidth);
    // leftLines.forEach((line, i) => {
    //   doc.text(line, boxLeftX + 5, startY + (i + 1) * lineHeight2);
    // });

    // doc.setFont("Inter-Regular", "normal");
    // let rightText = "* Computer Generated Document No sign Required";
    // const rightLines = doc.splitTextToSize(rightText, rightContentWidth);
    // rightLines.forEach((line, i) => {
    //   doc.text(line, boxRightX - 5, startY + i * lineHeight2, {
    //     align: "right",
    //   });
    // });
    //   let textY = footerY + 8; // Start after thank-you text
    //   if (terms?.length > 0) {
    //     doc.setFont("Inter-Bold", "bold");
    //     doc.setFontSize(9);
    //     doc.text("Terms & Conditions", 10, textY - 3);

    //   doc.setFont("Inter-Regular", "normal");
    //   doc.setFontSize(7);
    //   doc.text(terms, 13, textY);
    // }
    const rightX = pageWidth - padding;

    const boxTopY = currentY + 3;

    // Vertical positions
    const SIGN_Y = boxTopY; // Signature image
    const LABEL_Y = SIGN_Y + 8; // "Issued By, Signature"
    const STORE_Y = LABEL_Y + 4; // Store name

    // Fonts
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(8);

    // -------- 1️⃣ Signature Image FIRST ----------
    const signimg = uRLs.IMAGE_URL + storeSettings?.invoiceSettings?.signature;

    if (storeSettings?.invoiceSettings?.signature) {
      try {
        const response = await fetch(signimg);
        const blob = await response.blob();
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        const { width, height } = await loadImageDimensions(base64);

        const maxWidth = 50;
        const maxHeight = 10;

        const ratio = Math.min(maxWidth / width, maxHeight / height);
        const imgWidth = width * ratio;
        const imgHeight = height * ratio;

        const format = signimg.endsWith(".png") ? "PNG" : "JPEG";
        const imgX = rightX - imgWidth;

        doc.addImage(base64, format, imgX, SIGN_Y, imgWidth, imgHeight);
      } catch (err) {
        console.warn("Image failed to load:", err);
      }
    }

    // -------- 2️⃣ Issued By, Signature ----------
    doc.text("Issued By, Signature", rightX, LABEL_Y + 4, { align: "right" });

    // -------- 3️⃣ Store Name ----------
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);

    const titleText = String(globalData?.salefrom?.title || "");
    doc.text(titleText, rightX, STORE_Y + 4, { align: "right" });

    setDownload(false);
    // setPrint(false);
    return doc;
  };

  const handleDownload = async (data) => {
    setDownload(true);
    const { salemode, saleno } = data || {};

    let doc;

    if (salemode?.toUpperCase() === "BILL") {
      doc = await generateBillingPDF();
    } else {
      doc = await generateinvoicePDF();
    }

    const filenamePrefix = formatTrackMode(selectedSize)?.replace(/\s+/g, "_");
    doc.save(`${filenamePrefix}_${saleno}.pdf`);
  };

  const handlePrint = async (data, overrideSize = null) => {
    setPrint(true);
    try {
      if (!checkNetwork()) return;

      const paperSize = overrideSize || selectedSize;
      const upperMode = data?.salemode?.toUpperCase();
      let doc;

      if (paperSize === "BILL") {
        doc = await generateBillingPDF();
      } else {
        doc = await generateinvoicePDF();
      }

      const blob = doc.output("blob");
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }, 500);
      };
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setPrint(false);
    }
  };

  useEffect(() => {
    jsPDF.API.events.push([
      "addFonts",
      function () {
        this.addFileToVFS("Inter_24pt-Regular-normal.ttf", InterRegular);
        this.addFileToVFS("Inter_24pt-Bold-normal.ttf", InterBold);
        this.addFont(
          "Inter_24pt-Regular-normal.ttf",
          "Inter-Regular",
          "normal",
        );
        this.addFont("Inter_24pt-Bold-normal.ttf", "Inter-Bold", "bold");
      },
    ]);
  }, []);

  useEffect(() => {
    setData(isViewData);
  }, [isViewData]);
  useEffect(() => {
    setTerms(storeSettings?.invoiceSettings?.termsAndConditions);
  }, [storeSettings?.invoiceSettings]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "p") {
        event.preventDefault();
        handlePrint(data);
      }
    };
    const handleAfterPrint = () => {
      setTimeout(() => {
        window.focus();
      }, 100);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, [data]);

  return (
    <>
    <div className="invoice-scale">
      {selectedSize === "INVOICE" ? (
        <div className="tm_container">
          <div className="tm_invoice_wrap">
            <div className="tm_invoice tm_style1 tm_type1">
              <div className="tm_invoice_in">
                <table width="100%" className="pdfcontent">
                  <tbody>
                    <tr>
                      <td width="50%" style={{ textAlign: "left" }}>
                        <div className="tm_logo">
                          {("/assets/images/logo-light.png" ||
                            settings?.saleProfileData?.image) && (
                            <img
                              src={
                                settings?.saleProfileData?.image
                                  ? uRLs.IMAGE_URL +
                                    "profile/" +
                                    settings?.saleProfileData?.image
                                  : "/assets/images/logo-light.png"
                              }
                              className="img-rounded logo mb-0"
                              height="50"
                            />
                          )}
                        </div>
                      </td>
                      <td width="50%" style={{ textAlign: "right" }}>
                        <div className="tm_primary_color tm_f50 tm_text_uppercase">
                          {globalData?.salemode &&
                            formatTrackMode(globalData?.salemode)}
                        </div>
                        {globalData?.status === "CANCELLED" && (
                          <span className="badge rounded-pill bg-label-danger mt-3 fs-15 tm_text_uppercase">
                            {globalData?.status}
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="tm_invoice_info tm_mb20">
                  <div
                    className="tm_invoice_seperator tm_gray_bg"
                    style={{
                      backgroundColor:
                        selectedTheme?.templateTheme?.primaryColor,
                    }}
                  ></div>
                  <div className="tm_invoice_info_list">
                    <p className="tm_invoice_number tm_m0 mb-0 mt-3">
                      <b className="tm_primary_color">Invoice No: </b>{" "}
                      {globalData?.saleno}
                    </p>
                    <p className="tm_invoice_date tm_m0 mt-3">
                      <b className="tm_primary_color"> Date: </b>{" "}
                      {saleTime?.date}
                    </p>
                  </div>
                </div>
                <table width="100%" className="pdfcontent">
                  <tbody>
                    <tr>
                      <td width="30%" style={{ verticalAlign: "top" }}>
                        <div
                          style={{
                            fontSize: "14px",
                            lineHeight: "22px",
                            fontWeight: "600",
                            marginBottom: "0px",
                          }}
                        >
                          Invoice From:
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            marginBottom: "0px",
                          }}
                        >
                          {settings?.saleProfileData?.title || ""}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            lineHeight: "18px",
                            marginBottom: "0px",
                          }}
                        >
                          <div>
                            {settings?.saleProfileData?.addressline1 || ""}{" "}
                            {settings?.saleProfileData?.addressline2 || ""}{" "}
                            {settings?.saleProfileData?.district || ""}{" "}
                            {settings?.saleProfileData?.country || ""} -{" "}
                            {settings?.saleProfileData?.pincode || ""}
                          </div>
                          <div>
                            Phone: +91 {settings?.saleProfileData?.phone || ""}
                          </div>
                          <div>
                            Email: {settings?.saleProfileData?.email || ""}
                          </div>
                          {settings?.saleProfileData?.gstnumber && (
                            <div>
                              <strong>GST:</strong>{" "}
                              {settings?.saleProfileData?.gstnumber || ""}
                            </div>
                          )}
                          {settings?.saleProfileData?.fssai && (
                            <div>
                              <strong>FSSAI:</strong>{" "}
                              {settings?.saleProfileData?.fssai}
                            </div>
                          )}
                          {settings?.saleProfileData?.pannumber && (
                            <div>
                              <strong>PAN:</strong>{" "}
                              {settings?.saleProfileData?.pannumber || ""}
                            </div>
                          )}
                          <div>
                            <strong>State:</strong>{" "}
                            {settings?.saleProfileData?.state || ""} [ State
                            Code :{settings?.saleProfileData?.statecode || ""} ]
                          </div>
                        </div>
                      </td>

                      <td width="5%"></td>

                      <td width="30%" style={{ verticalAlign: "top" }}>
                        {globalData?.saleto &&
                          (globalData?.saleto?.name ||
                            globalData?.saleto?.addressline1 ||
                            globalData?.saleto?.addressline2 ||
                            globalData?.saleto?.state ||
                            globalData?.saleto?.district ||
                            globalData?.saleto?.country ||
                            globalData?.saleto?.pincode ||
                            globalData?.saleto?.phone ||
                            globalData?.saleto?.email ||
                            globalData?.saleto?.gstnumber ||
                            globalData?.saleto?.statecode ||
                            globalData?.saleto?.referid) && (
                            <div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  lineHeight: "22px",
                                  fontWeight: "600",
                                  marginBottom: "0px",
                                }}
                              >
                                Invoice To:
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  marginBottom: "0px",
                                }}
                              >
                                {globalData?.saleto?.name || ""}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  lineHeight: "18px",
                                  marginBottom: "0px",
                                }}
                              >
                                <div>
                                  {globalData?.saleto?.addressline1 || ""}{" "}
                                  {globalData?.saleto?.addressline2 || ""}{" "}
                                  {globalData?.saleto?.state || ""}{" "}
                                  {globalData?.saleto?.district || ""}{" "}
                                  {globalData?.saleto?.country || ""} -{" "}
                                  {globalData?.saleto?.pincode || ""}
                                </div>
                                {globalData?.saleto?.phone && (
                                  <div>
                                    Phone: +91 {globalData?.saleto?.phone}
                                  </div>
                                )}
                                {globalData?.saleto?.email && (
                                  <div>Email: {globalData?.saleto?.email}</div>
                                )}
                                {globalData?.saleto?.gstnumber && (
                                  <div>
                                    <strong>GST:</strong>{" "}
                                    {globalData?.saleto?.gstnumber}
                                  </div>
                                )}
                                {globalData?.saleto?.state && (
                                  <div>
                                    <strong>State:</strong>{" "}
                                    {globalData?.saleto?.state} [ State Code :
                                    {globalData?.saleto?.statecode || ""} ]
                                  </div>
                                )}
                                {globalData?.saleto?.referid && (
                                  <div>
                                    <strong>Reference ID:</strong>{" "}
                                    {globalData?.saleto?.referid}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                      </td>
                      <td width="5%"></td>
                      
                                      <td
                                        width="30%"
                                        style={{ verticalAlign: "top" }}
                                      >
                                        {globalData.saleto?.shippingAddress &&
                                          (globalData.saleto?.shippingAddress?.addressline1 ||
                                            globalData.saleto?.shippingAddress?.addressline2 ) && (
                                            <div>
                                              <div
                                                style={{
                                                  fontSize: "14px",
                                                  lineHeight: "22px",
                                                  fontWeight: "600",
                                                  marginBottom: "0px",
                                                }}
                                              >
                                                Shipping To:
                                              </div>
                                              <div
                                                style={{
                                                  fontSize: "12px",
                                                  lineHeight: "18px",
                                                  marginBottom: "0px",
                                                }}
                                              >
                                                <div>
                                                  {globalData.saleto
                                                    ?.shippingAddress?.addressline1 || ""}{" "}
                                                  {globalData.saleto
                                                    ?.shippingAddress?.addressline2 || ""}{" "}
                                                  {globalData.saleto?.shippingAddress?.state ||
                                                    ""}{" "}
                                                  {globalData.saleto
                                                    ?.shippingAddress?.district || ""}{" "}
                                                  {globalData.saleto?.shippingAddress?.country ||
                                                    ""}{" "}
                                                  -{" "}
                                                  {globalData.saleto?.shippingAddress?.pincode ||
                                                    ""}
                                                </div>
                                                {globalData.saleto?.shippingAddress?.state && (
                                                  <div>
                                                    <strong>State:</strong>{" "}
                                                    {globalData.saleto?.shippingAddress?.state} [
                                                    State Code :
                                                    {globalData.saleto
                                                      ?.shippingAddress?.statecode || ""}{" "}
                                                    ]
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                      </td>
                    </tr>
                  </tbody>
                </table>
                <table
                  // className="items"
                  width="100%"
                  style={{
                    marginTop: "6px",
                    borderCollapse: "collapse",
                  }}
                  cellPadding="4"
                >
                  <thead>
                    <tr>
                      <th
                        className="force-style"
                        style={{
                          width: "20px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        S.No
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "240px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Description
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "120px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Rate
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "50px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Qty
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "100px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Gross Amount
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "100px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Taxable Value
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "30px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        GST(%)
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "100px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        GST Amount
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "100px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {globalData?.saleitems?.map((product, index) => (
                      <tr key={index}>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "left",
                            fontSize: "12px",
                            width: "30%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          <div>{product.title}</div>
                          {product.imode === "SERVICE" && (
                            <div className="ms-0 fsz-10 lh-13 mb-1">
                              {product.description}
                            </div>
                          )}
                          {product.imode === "PRODUCT" && product.note && (
                            <div className="ms-0 fsz-10 mb-1">
                              {product?.note}
                            </div>
                          )}
                          {product.hsnsac && (
                            <div>
                              <span className="fw-medium">
                                {" "}
                                {product.imode === "SERVICE" ? "SAC:" : "HSN:"}
                              </span>{" "}
                              {product.hsnsac}
                            </div>
                          )}
                          {product.discount > 0 && (
                            <>
                              <div>
                                <span className="fw-medium">MRP : </span> ₹
                                {CurrencyConverter.toCurrency(
                                  product.mrp || 0,
                                  "INR",
                                )}
                              </div>
                              <div>
                                <span className="fw-medium">Discount : </span> -
                                ₹{product.discount}
                              </div>
                            </>
                          )}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹{product.soldprice }
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product.quantity} {
                            (() => {
                              const unitCode = product?.isSecondaryUnit
                                ? product?.unitConfig?.secondaryUnitCode
                                : product?.unitConfig?.primaryUnitCode;
                    
                              // return unitCode !== "unit" ? unitCode : "";
                              return unitCode||"" ;
                            })()
                          }
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹{product.lineTotalSoldPrice}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹{product.lineTotalBasePrice}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product.taxpercentage}%
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹{product.lineTotalTaxAmount}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹{product.lineTotalSoldPrice}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td
                        colSpan={2}
                        className="force-style"
                        style={{
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      ></td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Total
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹{globalData?.totalsoldprice}
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹{globalData?.totalbaseprice }
                      </td>
                      <td
                        className="force-style"
                        style={{
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      ></td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹{globalData?.totaltaxamount}
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹{globalData?.grandtotal }
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="5"
                      >
                        Total Amount In Words
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="3"
                      >
                        Total Amount Before Tax
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹{globalData?.totalbaseprice}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="5"
                        rowSpan="5"
                      >
                        {globalData?.amountinwords}
                      </td>
                    </tr>
                    {globalData?.salefrom?.statecode &&
                    globalData?.saleto?.statecode &&
                    globalData?.salefrom?.statecode !==
                      globalData?.saleto?.statecode ? (
                      <tr>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "right",
                            fontSize: "12px",
                            fontWeight: 500,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                          colSpan="3"
                        >
                          IGST
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            fontWeight: 500,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹{globalData?.igstamount}
                        </td>
                      </tr>
                    ) : (
                      <>
                        <tr>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              fontWeight: 500,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                            colSpan="3"
                          >
                            SGST
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              fontWeight: 500,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            ₹{globalData?.sgstamount}
                          </td>
                        </tr>
                        <tr>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              fontWeight: 500,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                            colSpan="3"
                          >
                            CGST
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              fontWeight: 500,
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            ₹{globalData?.cgstamount}
                          </td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="3"
                      >
                        TOTAL
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹{globalData?.totalsoldprice }
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "14px",
                          fontWeight: 600,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="3"
                      >
                        Grand Total (Rounded)
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "14px",
                          fontWeight: 600,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹{globalData?.grandtotal}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table width="100%">
                  <tbody>
                    <tr>
                      <td
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          textAlign: "center",
                          padding: "8px",
                          color: "black",
                        }}
                      >
                        Your Total Discount : ₹
                        {globalData?.totaldiscount}
                        {/* <br />
                        <div className="mt-1 fs-20">
                          ***** Thank You! Visit Us Again *****
                        </div> */}
                      </td>
                    </tr>
                  </tbody>
                </table>
                { globalData?.grandtotal >0 &&(
                            <table
                            className="items"
                            width="38%"
                            style={{
                              marginTop: "4px",
                              // borderCollapse: "collapse",
                            }}
                            cellPadding="4"
                          >
                            <thead>
                              <tr>
                                <th
                                  colSpan={2}
                                  className="force-style"
                                  style={{
                                    width: "100%",
                                    textAlign: "center",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                                      "--th-color": selectedTheme?.templateTheme?.foregroundColor,
                                  }}
                                >
                                  Payment Mode
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {globalData.cashPaid>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>Cash</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    fontWeight:"600",
                                    width: "10%",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  ₹{globalData.cashPaid}
                                </td>
                              </tr>)}
                              {globalData.cardPaid>0&&(
                              <tr>
                                <td  className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>Card</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    fontWeight:"600",
                                    width: "10%",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  ₹{globalData.cardPaid}
                                </td>
                              </tr>)}
                              {globalData.onlinePaid>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>OnlineAmount</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    width: "10%",
                                    fontWeight:"600",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  ₹{globalData.onlinePaid}
                                </td>
                              </tr>)}
                              {globalData?.upiPaid>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>UPI</td>
                                <td
                                 className="force-style"
                                 style={{
                                   textAlign: "start",
                                   fontSize: "12px",
                                   width: "10%",
                                   fontWeight:"600",
                                   "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                 }}
                                >
                                  ₹{globalData.upiPaid}
                                </td>
                              </tr>)}
                              {globalData.balanceDue>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>BalanceDue</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    width: "10%",
                                    fontWeight:"600",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  ₹{globalData.balanceDue}
                                </td>
                              </tr>)}
                            </tbody>
                          </table>
                            )}
                              <div className="mt-2 fs-20 d-flex justify-content-center fw-bold">
                                      <span
                                      >
                                        ***** Thank You! Visit Us Again *****
                                      </span>
                                    </div>
                                    {storeSettings?.negobillBranding && (
                                      <>
                                    <div className="mt-1 fs-20 d-flex justify-content-center fw-bold">
                                      <span
                                      >
                                        Powered By
                                      </span>
                                    </div>
                                    <div className=" fs-20 d-flex justify-content-center fw-bold">
                                      <span
                                      >
                                        www.NegoBill.com
                                      </span>
                                    </div>
                                    </>)}
                                    <table width="100%">
                                <tbody>
                                  <tr>
                                  {terms?.length > 0 && (
                                    <td
                                      style={{
                                        padding: "8px",
                                      }}
                                      width="70%"
                                    >
                                      <div
                                        className="fs-13 fw-600"
                                        style={{
                                          textAlign: "left",
                                          color: "black",
                                        }}
                                      >
                                        Terms & Conditions
                                      </div>
                                      <div className="terms-box">{terms}</div>
                                    </td>
                                      )}
                                    <td
                                      style={{
                                        padding: "8px",
                                        marginTop: "15px",
                                      }}
                                      className="d-flex align-items-start justify-content-end"
                                    >
                                      <div>
                                      {storeSettings?.invoiceSettings
                                          ?.signature && (
                                          <div className="text-end p-0 fw-bold ">
                                            <img
                                              src={
                                                uRLs.IMAGE_URL +
                                                storeSettings?.invoiceSettings
                                                  ?.signature
                                              }
                                              width={150}
                                            />
                                          </div>
                                        )}
                                        <div className="text-end p-0">
                                          Issued By,Signature
                                        </div>
                                        <div className="text-end p-0 fw-bold ">
                                          {globalData?.salefrom?.title}
                                        </div>
                                        
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                {/* <table
                  
                  width="100%"
                  style={{ borderCollapse: "collapse" }}
                  cellPadding="8"
                >
                  <tbody>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          verticalAlign: "middle",
                          width: "52.5%",
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          textAlign: "left",
                          padding: "8px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          Declaration:
                        </span>
                        <br />
                        <span style={{ fontSize: "12px" }}>
                          We declare that this Tax Invoice shows the actual
                          price of the goods described and that all particulars
                          are true and correct.
                        </span>
                      </td>
                      <td
                        className="force-style"
                        style={{
                          width: "50%",
                          fontSize: "12px",
                          fontWeight: "500",
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          textAlign: "center",
                          padding: "8px",
                        }}
                      >
                        * Computer Generated Document No sign Required
                      </td>
                    </tr>
                  </tbody>
                </table> */}
              </div>
            </div>
          </div>
        </div>
      ) : selectedSize === "BILL" ? (
        <div className="tm_container">
          <div className="tm_invoice_wrap">
            <div className="tm_invoice tm_style1 tm_type1">
              <div className="tm_invoice_in">
                <table width="100%" className="pdfcontent">
                  <tbody>
                    <tr>
                      <td width="50%" style={{ textAlign: "left" }}>
                        <div className="tm_logo">
                          {(settings?.saleProfileData?.image ||
                            "/assets/images/logo-light.png") && (
                            <img
                              src={
                                settings?.saleProfileData?.image
                                  ? uRLs.IMAGE_URL +
                                    "profile/" +
                                    settings?.saleProfileData?.image
                                  : "/assets/images/logo-light.png"
                              }
                              className="img-rounded logo mb-0"
                              height="50"
                            />
                          )}
                        </div>
                      </td>
                      <td width="50%" style={{ textAlign: "right" }}>
                        <div className="tm_primary_color tm_f50 tm_text_uppercase">
                          {selectedSize && formatTrackMode(selectedSize)}
                        </div>
                        {isViewData.status === "CANCELLED" && (
                          <span className="badge rounded-pill bg-label-danger mt-3 fs-15 tm_text_uppercase">
                            {isViewData.status}
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="tm_invoice_info tm_mb20">
                  <div
                    className="tm_invoice_seperator tm_gray_bg"
                    style={{
                      backgroundColor:
                        selectedTheme?.templateTheme?.primaryColor,
                    }}
                  ></div>
                  <div className="tm_invoice_info_list">
                    <p className="tm_invoice_number tm_m0 mb-0 mt-3">
                      <b className="tm_primary_color">Bill No: </b>{" "}
                      {isViewData.saleno}
                    </p>
                    <p className="tm_invoice_date tm_m0 mt-3">
                      <b className="tm_primary_color"> Date: </b>{" "}
                      {saleTime.date}
                    </p>
                  </div>
                </div>
                <table width="100%" className="pdfcontent">
                  <tbody>
                    <tr>
                      <td width="45%" style={{ verticalAlign: "top" }}>
                        <div
                          style={{
                            fontSize: "14px",
                            lineHeight: "22px",
                            fontWeight: "600",
                            marginBottom: "0px",
                          }}
                        >
                          Bill From:
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: "600",
                            marginBottom: "0px",
                          }}
                        >
                          {settings?.saleProfileData?.title || ""}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            lineHeight: "18px",
                            marginBottom: "0px",
                          }}
                        >
                          <div>
                            {settings?.saleProfileData?.addressline1 || ""}{" "}
                            {settings?.saleProfileData?.addressline2 || ""}{" "}
                            {settings?.saleProfileData?.state || ""}{" "}
                            {settings?.saleProfileData?.district || ""}{" "}
                            {settings?.saleProfileData?.country || ""} -{" "}
                            {settings?.saleProfileData?.pincode || ""}
                          </div>
                          <div>
                            Phone: +91 {settings?.saleProfileData?.phone || ""}
                          </div>
                          <div>
                            Email: {settings?.saleProfileData?.email || ""}
                          </div>
                          {settings?.saleProfileData?.gstnumber && (
                            <div>
                              <strong>GST:</strong>{" "}
                              {settings?.saleProfileData?.gstnumber || ""}
                            </div>
                          )}
                          {settings?.saleProfileData?.fssai && (
                            <div>
                              <strong>FSSAI:</strong>{" "}
                              {settings?.saleProfileData?.fssai}
                            </div>
                          )}
                          {settings?.saleProfileData?.pannumber && (
                            <div>
                              <strong>PAN:</strong>{" "}
                              {settings?.saleProfileData?.pannumber || ""}
                            </div>
                          )}
                          <div>
                            <strong>State:</strong>{" "}
                            {settings?.saleProfileData?.state || ""} [ State
                            Code: {settings?.saleProfileData?.statecode || ""} ]
                          </div>
                        </div>
                      </td>

                      <td width="10%"></td>

                      <td width="45%" style={{ verticalAlign: "top" }}>
                        {isViewData.saleto &&
                          (isViewData.saleto.name ||
                            isViewData.saleto.addressline1 ||
                            isViewData.saleto.addressline2 ||
                            isViewData.saleto.state ||
                            isViewData.saleto.district ||
                            isViewData.saleto.country ||
                            isViewData.saleto.pincode ||
                            isViewData.saleto.phone ||
                            isViewData.saleto.email ||
                            isViewData.saleto.gstnumber ||
                            isViewData.saleto.statecode ||
                            isViewData.saleto.referid) && (
                            <div>
                              <div
                                style={{
                                  fontSize: "14px",
                                  lineHeight: "22px",
                                  fontWeight: "600",
                                  marginBottom: "0px",
                                }}
                              >
                                Bill To:
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  marginBottom: "0px",
                                }}
                              >
                                {isViewData.saleto?.name || ""}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  lineHeight: "18px",
                                  marginBottom: "0px",
                                }}
                              >
                                <div>
                                  {isViewData.saleto?.addressline1 || ""}{" "}
                                  {isViewData.saleto?.addressline2 || ""}{" "}
                                  {isViewData.saleto?.state || ""}{" "}
                                  {isViewData.saleto?.district || ""}{" "}
                                  {isViewData.saleto?.country || ""} -{" "}
                                  {isViewData.saleto?.pincode || ""}
                                </div>
                                {isViewData.saleto?.phone && (
                                  <div>
                                    Phone: +91 {isViewData.saleto?.phone}
                                  </div>
                                )}
                                {isViewData.saleto?.email && (
                                  <div>Email: {isViewData.saleto?.email}</div>
                                )}
                                {isViewData.saleto?.gstnumber && (
                                  <div>
                                    <strong>GST:</strong>{" "}
                                    {isViewData.saleto?.gstnumber}
                                  </div>
                                )}
                                {isViewData.saleto?.state && (
                                  <div>
                                    <strong>State:</strong>{" "}
                                    {isViewData.saleto?.state} [ State Code :
                                    {isViewData.saleto?.statecode || ""} ]
                                  </div>
                                )}
                                {isViewData.saleto?.referid && (
                                  <div>
                                    <strong>Reference ID:</strong>{" "}
                                    {isViewData.saleto?.referid}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table
                  // className="items"
                  width="100%"
                  style={{
                    marginTop: "6px",
                    borderCollapse: "collapse",
                  }}
                  cellPadding="4"
                >
                  <thead>
                    <tr>
                      <th
                        className="force-style"
                        style={{
                          width: "20px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          // border: `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                        }}
                      >
                        S.No
                      </th>
                      <th
                        className="force-style"
                        style={{
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Description
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "60px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Rate (Per Unit)
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "60px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Qty
                      </th>
                      <th
                        className="force-style"
                        style={{
                          width: "60px",
                          textAlign: "center",
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {isViewData.saleitems?.map((product, index) => (
                      <tr key={index}>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {index + 1}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "left",
                            fontSize: "12px",
                            width: "45%",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          <div>{product.title}</div>
                          {product.imode === "SERVICE" && (
                            <div className="ms-0 fsz-10 lh-13 mb-1">
                              {product.description}
                            </div>
                          )}
                          {product.imode === "PRODUCT" && product.note && (
                            <div className="ms-0 fs-12 text-truncate-description text-muted">
                              {product?.note}
                            </div>
                          )}
                          {product.hsnsac && (
                            <div>
                              <span className="fw-medium">
                                {" "}
                                {product.imode === "SERVICE"
                                  ? "SAC:"
                                  : "HSN:"}{" "}
                              </span>{" "}
                              {product.hsnsac}
                            </div>
                          )}
                          {product.discount > 0 && (
                            <>
                              <div>
                                <span className="fw-medium">MRP : </span> ₹
                                {CurrencyConverter.toCurrency(
                                  product.mrp || 0,
                                  "INR",
                                )}
                              </div>
                              <div>
                                <span className="fw-medium">Discount : </span> -
                                ₹
                                {CurrencyConverter.toCurrency(
                                  product.discount || 0,
                                  "INR",
                                )}
                              </div>
                            </>
                          )}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹
                          {CurrencyConverter.toCurrency(
                            product.soldprice || 0,
                            "INR",
                          )}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          {product.quantity}
                        </td>
                        <td
                          className="force-style"
                          style={{
                            textAlign: "center",
                            fontSize: "12px",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          ₹
                          {CurrencyConverter.toCurrency(
                            product.lineTotalSoldPrice || 0,
                            "INR",
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor}`,
                        }}
                        colSpan={2}
                      ></td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        Total
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        {isViewData.totalqty}
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹
                        {CurrencyConverter.toCurrency(
                          isViewData.totalsoldprice || 0,
                          "INR",
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "left",
                          fontSize: "12px",
                          fontWeight: 600,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="2"
                      >
                        Total Amount In Words
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="2"
                      >
                        Grand Total
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹
                        {CurrencyConverter.toCurrency(
                          isViewData.grandtotal || 0,
                          "INR",
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="2"
                        rowSpan="5"
                      >
                        {isViewData.amountinwords}
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "right",
                          fontSize: "14px",
                          fontWeight: 600,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                        colSpan="2"
                      >
                        Grand Total (Rounded)
                      </td>
                      <td
                        className="force-style"
                        style={{
                          textAlign: "center",
                          fontSize: "14px",
                          fontWeight: 600,
                          "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                          "--th-color":
                            selectedTheme?.templateTheme?.foregroundColor,
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                        }}
                      >
                        ₹
                        {CurrencyConverter.toCurrency(
                          isViewData.grandtotal || 0,
                          "INR",
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table width="100%">
                  <tbody>
                    <tr>
                      <td
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          textAlign: "center",
                          padding: "8px",
                          color: "black",
                        }}
                      >
                        Your Total Discount : ₹
                        {CurrencyConverter.toCurrency(
                          isViewData.totaldiscount || 0,
                          "INR",
                        )}
                        {/* <br />
                        <div className="mt-1 fs-20">
                          ***** Thank You! Visit Us Again *****
                        </div> */}
                      </td>
                    </tr>
                  </tbody>
                </table>
                { data?.grandtotal >0 &&(
                            <table
                            className="items"
                            width="38%"
                            style={{
                              marginTop: "4px",
                              // borderCollapse: "collapse",
                            }}
                            cellPadding="4"
                          >
                            <thead>
                              <tr>
                                <th
                                  colSpan={2}
                                  className="force-style"
                                  style={{
                                    width: "100%",
                                    textAlign: "center",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                                      "--th-bg": selectedTheme?.templateTheme?.primaryColor,
                                      "--th-color": selectedTheme?.templateTheme?.foregroundColor,
                                  }}
                                >
                                  Payment Mode
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {((data?.cashamount || 0) - (data?.cashchange || 0))>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>Cash</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    fontWeight:"600",
                                    width: "10%",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  {formatCurrency(
                            (data?.cashamount || 0) - (data?.cashchange || 0)
                          )}
                                </td>
                              </tr>)}
                              {((data.cardamount || 0) - (data.cardchange || 0))>0&&(
                              <tr>
                                <td  className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>Card</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    fontWeight:"600",
                                    width: "10%",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  {formatCurrency(
                            (data.cardamount || 0) - (data.cardchange || 0)
                          )}
                                </td>
                              </tr>)}
                              {((data.onlineamount || 0) - (data.onlinechange || 0))>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>OnlineAmount</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    width: "10%",
                                    fontWeight:"600",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  {formatCurrency(
                            (data.onlineamount || 0) - (data.onlinechange || 0)
                          )}
                                </td>
                              </tr>)}
                              {((data.upiamount || 0) - (data.upichange || 0))>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>UPI</td>
                                <td
                                 className="force-style"
                                 style={{
                                   textAlign: "start",
                                   fontSize: "12px",
                                   width: "10%",
                                   fontWeight:"600",
                                   "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                 }}
                                >
                                  {formatCurrency(
                            (data.upiamount || 0) - (data.upichange || 0)
                          )}
                                </td>
                              </tr>)}
                              {((data.paylateramount || 0) - (data.paylaterchange || 0))>0 &&(
                              <tr>
                                <td className="force-style"
                                      style={{
                                        textAlign: "start",
                                        fontSize: "12px",
                                        width: "10%",
                                        "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                      }}>BalanceDue</td>
                                <td
                                  className="force-style"
                                  style={{
                                    textAlign: "start",
                                    fontSize: "12px",
                                    width: "10%",
                                    fontWeight:"600",
                                    "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `
                                  }}
                                >
                                  {formatCurrency(
                            (data.paylateramount || 0) -
                              (data.paylaterchange || 0)
                          )}
                                </td>
                              </tr>)}
                            </tbody>
                          </table>
                            )}
                              <div className="mt-2 fs-20 d-flex justify-content-center fw-bold">
                                      <span
                                      >
                                        ***** Thank You! Visit Us Again *****
                                      </span>
                                    </div>
                                    {storeSettings?.negobillBranding && (
                                      <>
                                    <div className="mt-1 fs-20 d-flex justify-content-center fw-bold">
                                      <span
                                      >
                                        Powered By
                                      </span>
                                    </div>
                                    <div className=" fs-20 d-flex justify-content-center fw-bold">
                                      <span
                                      >
                                        www.NegoBill.com
                                      </span>
                                    </div>
                                    </>)}
                                    <table width="100%">
                                <tbody>
                                  <tr>
                                  {terms?.length > 0 && (
                                    <td
                                      style={{
                                        padding: "8px",
                                      }}
                                      width="70%"
                                    >
                                      <div
                                        className="fs-13 fw-600"
                                        style={{
                                          textAlign: "left",
                                          color: "black",
                                        }}
                                      >
                                        Terms & Conditions
                                      </div>
                                      <div className="terms-box">{terms}</div>
                                    </td>
                                      )}
                                    <td
                                      style={{
                                        padding: "8px",
                                        marginTop: "15px",
                                      }}
                                      className="d-flex align-items-start justify-content-end"
                                    >
                                      <div>
                                      {storeSettings?.invoiceSettings
                                          ?.signature && (
                                          <div className="text-end p-0 fw-bold ">
                                            <img
                                              src={
                                                uRLs.IMAGE_URL +
                                                storeSettings?.invoiceSettings
                                                  ?.signature
                                              }
                                              width={150}
                                            />
                                          </div>
                                        )}
                                        <div className="text-end p-0">
                                          Issued By,Signature
                                        </div>
                                        <div className="text-end p-0 fw-bold ">
                                          {data?.salefrom?.title}
                                        </div>
                                        
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                {/* <table
                  
                  width="100%"
                  style={{ borderCollapse: "collapse" }}
                  cellPadding="8"
                >
                  <tbody>
                    <tr>
                      <td
                        className="force-style"
                        style={{
                          verticalAlign: "middle",
                          width: "52.5%",
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          textAlign: "left",
                          padding: "8px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          Declaration:
                        </span>
                        <br />
                        <span style={{ fontSize: "12px" }}>
                          We declare that this bill recipet shows the actual
                          price of the goods described and that all particulars
                          are true and correct.
                        </span>
                      </td>
                      <td
                        className="force-style"
                        style={{
                          width: "50%",
                          fontSize: "12px",
                          fontWeight: "500",
                          "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          textAlign: "center",
                          padding: "8px",
                        }}
                      >
                        * Computer Generated Document No sign Required
                      </td>
                    </tr>
                  </tbody>
                </table> */}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="default-content">
          <p>No content available for this sale mode.</p>
        </div>
      )}
      </div>
      <div
        className="modal-footer d-block mt-2"
        style={{
          position: "sticky",
          bottom: -4,
          background: "#fff",
          zIndex: 10,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        {payment && (
        <div className="fw-600 mb-2 font-14">PAYMENT SUMMARY</div>)}
        {payment && (
        <table
          className="items table-bordered mb-3"
          width="100%"
          cellPadding="4"
        >
          <tbody className="text-center">
            <tr>
              <th
                style={{
                  textAlign: "center",
                  background: "#f3f3f3",
                  border: "0.5mm solid #e0e0e0",
                  cursor: isViewData?.cashamount > 0 ? "pointer" : "default",
                  padding: "2px 8px",
                }}
                onClick={(e) => {
                  if (isViewData?.cashamount > 0) {
                    cashRef.current.openPopover(e);
                  }
                }}
              >
                <div
                  className={
                    isViewData?.cashamount > 0
                      ? "badge rounded-pill bg-label-moon d-inline-flex align-items-center"
                      : "d-inline-flex align-items-center"
                  }
                  style={{ gap: "4px",fontSize:"12px" }}
                >
                  CASH
                  {isViewData?.cashamount > 0 && <ShowIcon/>}
                </div>
              </th>
              <th
                style={{
                  textAlign: "center",
                  background: "#f3f3f3",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
              >
                CARD
              </th>
              <th
                style={{
                  textAlign: "center",
                  background: "#f3f3f3",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
              >
                ONLINE
              </th>
              <th
                style={{
                  textAlign: "center",
                  background: "#f3f3f3",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
              >
                UPI
              </th>
              <th
                style={{
                  textAlign: "center",
                  background: "#f3f3f3",
                  border: "0.5mm solid #e0e0e0",
                  cursor:
                    isViewData?.paylateramount > 0 ? "pointer" : "default",
                  padding: "2px 8px",
                  fontSize:"12px"
                }}
                onClick={(e) => {
                  if (isViewData?.paylateramount > 0) {
                    paylaterRef.current.openPopover(e);
                  }
                }}
              >
                <div
                  className={
                    isViewData?.paylateramount > 0
                      ? "badge rounded-pill bg-label-fire d-inline-flex align-items-center"
                      : "d-inline-flex align-items-center"
                  }
                  style={{ gap: "4px" }}
                >
                  PAYLATER
                  {isViewData?.paylateramount > 0 && (
                    <ShowIcon iClass={"ms-1"}/>
                  )}
                </div>
              </th>
            </tr>
            <tr>
              <td
                style={{
                  textAlign: "center",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
                className="text-center"
              >
                {formatCurrency(
                  (isViewData?.cashamount || 0) - (isViewData?.cashchange || 0),
                )}
              </td>
              <td
                style={{
                  textAlign: "center",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
                className="text-center"
              >
                ₹
                {CurrencyConverter.toCurrency(
                  (isViewData?.cardamount || 0) - (isViewData?.cardchange || 0),
                  "INR",
                )}
              </td>
              <td
                style={{
                  textAlign: "center",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
                className="text-center"
              >
                {formatCurrency(
                  (isViewData?.onlineamount || 0) -
                    (isViewData?.onlinechange || 0),
                )}
              </td>
              <td
                style={{
                  textAlign: "center",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
                className="text-center"
              >
                {formatCurrency(
                  (isViewData?.upiamount || 0) - (isViewData?.upichange || 0),
                )}
              </td>
              <td
                style={{
                  textAlign: "center",
                  border: "0.5mm solid #e0e0e0",
                  fontSize:"12px"
                }}
                className="text-center"
              >
                {formatCurrency(
                  (isViewData?.paylateramount || 0) -
                    (isViewData?.paylaterchange || 0),
                )}
              </td>
            </tr>
          </tbody>
          <CashButton
            ref={cashRef}
            trackId={isViewData?.boxCashInfo?.trackid}
            trackMode={isViewData?.boxCashInfo?.trackmode}
          />
          <PayLaterButton
            ref={paylaterRef}
            trackId={isViewData?.paylaterInfo?.trackid}
            customerId={isViewData?.paylaterInfo?.customerid}
            trackMode={isViewData?.paylaterInfo?.trackmode}
          />
        </table>)}
        {downloadbtn && (
          <div className="my-2 d-flex gap-2">
            <div className="btn-group text-right align-items-center">
              {print ? (
                <button className="btn bg-label-animal btn-sm pb-0 font-12" disabled>
                  <CircularProgress size={12} className="text-white" />
                </button>
              ) : (
                <>
                  <button
                    onClick={() => handlePrint(data)}
                    className="btn bg-label-animal shadow-none btn-sm font-12"
                    id="printreceiptbtn"
                  >
                    <PrinterIcon iClass={"fs-12"}/> Print 
                  </button>
                </>
              )}
            </div>

            {download ? (
              <button
                className="btn btn-warning btn-sm pb-0 text-right"
                disabled
              >
                <CircularProgress size={18} className="text-black" />
              </button>
            ) : (
              <button
                className="btn btn-warning btn-sm text-right"
                id="generatebillpdfbtn"
                onClick={() => handleDownload(data)}
              >
                <DownloadIcon iClass={"fs-15"}/>
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default InvoiceThumnail1;
